// ─── Alert Evaluator ──────────────────────────────────────────────────────────
// Pure function module that evaluates behavior data against thresholds
// and generates AlertLogEntry objects.

import {
  GanttBarData,
  BehaviorType,
  ThresholdConfig,
  BehaviorThreshold,
  ThresholdLevel,
  AlertBehaviorKey,
  AlertBehaviorType,
  FrequencyWindow,
  windowToMinutes,
  fmtFreqLabel,
} from './behaviorTypes';
import { AlertLogEntry, AlertMetricType } from './AlertHistoryPanel';

// ─── BehaviorType → AlertBehaviorKey mapping ──────────────────────────────────

const ALERT_KEY_TO_BEHAVIOR_TYPE: Record<AlertBehaviorKey, BehaviorType> = {
  spin: 'spin',
  scratching: 'scratch',
  walking: 'walk',
  standing: 'standing',
  sitting: 'sitting',
  lying: 'lying',
  rest: 'rest',
  toilet: 'toilet',
};

// ─── Helper: compute metrics from gantt bars ──────────────────────────────────

interface BehaviorMetrics {
  /** Number of distinct bars (episodes) of this behavior in the time range. */
  frequency: number;
  /** Duration of the longest continuous bar for this behavior, in minutes. */
  maxDuration: number;
  /** Total duration of all bars for this behavior, in minutes. */
  totalDuration: number;
}

/**
 * Compute frequency and duration metrics for a given behavior type
 * from the gantt bars within the specified time range.
 */
function computeMetrics(
  bars: GanttBarData[],
  behaviorKey: AlertBehaviorKey,
  timeRangeStartMs: number,
  timeRangeEndMs: number,
): BehaviorMetrics {
  const behaviorType = ALERT_KEY_TO_BEHAVIOR_TYPE[behaviorKey];
  const filteredBars = bars.filter(
    (bar) =>
      bar.type === behaviorType &&
      bar.endTime > timeRangeStartMs &&
      bar.startTime < timeRangeEndMs,
  );

  let maxDuration = 0;
  let totalDuration = 0;

  for (const bar of filteredBars) {
    // Clamp bar to time range
    const effectiveStart = Math.max(bar.startTime, timeRangeStartMs);
    const effectiveEnd = Math.min(bar.endTime, timeRangeEndMs);
    const durationMs = effectiveEnd - effectiveStart;
    const durationMin = durationMs / 60000;
    if (durationMin > maxDuration) maxDuration = durationMin;
    totalDuration += durationMin;
  }

  return {
    frequency: filteredBars.length,
    maxDuration: Math.round(maxDuration * 10) / 10,
    totalDuration: Math.round(totalDuration * 10) / 10,
  };
}

// ─── Helper: compute frequency per window ───────────────────────────────────

/** Compute the event rate normalized to the given window (in minutes).
 *  E.g. if 10 events occurred in 30min and window is 5min, returns ~1.67 events/5min.
 */
function frequencyPerWindow(count: number, timeRangeMinutes: number, windowMinutes: number): number {
  if (timeRangeMinutes <= 0 || windowMinutes <= 0) return 0;
  return Math.round((count / timeRangeMinutes) * windowMinutes * 10) / 10;
}

// ─── Helper: format alert title ────────────────────────────────────────────────

const BEHAVIOR_LABELS_FOR_ALERTS: Record<AlertBehaviorKey, string> = {
  sitting: 'Sitting',
  standing: 'Standing',
  lying: 'Lying',
  walking: 'Walking',
  scratching: 'Scratching',
  spin: 'Spinning',
  toilet: 'Toilet',
  rest: 'Resting',
};

function formatAlertTitle(
  behaviorKey: AlertBehaviorKey,
  severity: 'warning' | 'critical',
  metricType: AlertMetricType,
): string {
  const label = BEHAVIOR_LABELS_FOR_ALERTS[behaviorKey];
  const severityLabel = severity === 'critical' ? 'Critical' : 'Warning';

  if (metricType === 'frequency') {
    return `${severityLabel}: High ${label} Frequency`;
  }
  if (metricType === 'duration') {
    return `${severityLabel}: Prolonged ${label}`;
  }
  return `${severityLabel}: ${label} Alert`;
}

function formatAlertMessage(
  behaviorKey: AlertBehaviorKey,
  _severity: 'warning' | 'critical',
  metricType: AlertMetricType,
  actualValue: number,
  threshold: number,
  timeRangeMinutes: number,
  window?: FrequencyWindow,
): string {
  const label = BEHAVIOR_LABELS_FOR_ALERTS[behaviorKey];

  if (metricType === 'frequency') {
    const windowMin = windowToMinutes(window);
    const perWindow = frequencyPerWindow(actualValue, timeRangeMinutes, windowMin);
    const unitLabel = fmtFreqLabel(threshold, window).replace(/^\d+\//, '/');
    return `Detected ${perWindow} ${label.toLowerCase()} episodes${unitLabel} (threshold: ${fmtFreqLabel(threshold, window)})`;
  }
  if (metricType === 'duration') {
    return `Dog has been ${label.toLowerCase()} for ${actualValue} min (threshold: ${threshold} min)`;
  }
  return `${label} alert triggered`;
}

// ─── Main evaluator ───────────────────────────────────────────────────────────

export interface EvaluateAlertsOptions {
  ganttBars: GanttBarData[];
  thresholds: ThresholdConfig;
  /** Start of time range in epoch ms */
  timeRangeStartMs: number;
  /** End of time range in epoch ms */
  timeRangeEndMs: number;
}

/**
 * Evaluate behavior data against thresholds and generate AlertLogEntry objects.
 *
 * For each enabled behavior, checks both warning and critical thresholds.
 * If a critical threshold is exceeded, only the critical alert is generated
 * (not a duplicate warning alert for the same metric).
 */
export function evaluateAlerts(options: EvaluateAlertsOptions): AlertLogEntry[] {
  const { ganttBars, thresholds, timeRangeStartMs, timeRangeEndMs } = options;
  const alerts: AlertLogEntry[] = [];
  const timeRangeMinutes = (timeRangeEndMs - timeRangeStartMs) / 60000;

  if (timeRangeMinutes <= 0) return alerts;

  const allBehaviorKeys: AlertBehaviorKey[] = [
    'sitting', 'standing', 'lying', 'walking', 'scratching', 'spin', 'toilet', 'rest',
  ];

  for (const behaviorKey of allBehaviorKeys) {
    const config = thresholds[behaviorKey];
    if (!config.enabled) continue;

    const metrics = computeMetrics(ganttBars, behaviorKey, timeRangeStartMs, timeRangeEndMs);

    // Check critical first — if critical is exceeded, skip warning for same metric
    const criticalAlerts = checkThresholdLevel(
      config.critical,
      'critical',
      behaviorKey,
      metrics.frequency,
      metrics.maxDuration,
      timeRangeMinutes,
    );

    const warningAlerts = checkThresholdLevel(
      config.warning,
      'warning',
      behaviorKey,
      metrics.frequency,
      metrics.maxDuration,
      timeRangeMinutes,
    );

    // For each metric type, only include warning if critical was NOT triggered
    for (const warningAlert of warningAlerts) {
      const hasCriticalForSameMetric = criticalAlerts.some(
        (ca) => ca.metricType === warningAlert.metricType,
      );
      if (!hasCriticalForSameMetric) {
        alerts.push(warningAlert);
      }
    }

    alerts.push(...criticalAlerts);
  }

  return alerts;
}

function checkThresholdLevel(
  level: ThresholdLevel | undefined,
  severity: 'warning' | 'critical',
  behaviorKey: AlertBehaviorKey,
  eventCount: number,
  maxDurationMin: number,
  timeRangeMinutes: number,
): AlertLogEntry[] {
  if (!level) return [];

  const alerts: AlertLogEntry[] = [];
  const behaviorType = ALERT_KEY_TO_BEHAVIOR_TYPE[behaviorKey] as AlertBehaviorType;
  const windowMin = windowToMinutes(level.frequencyWindow);

  // Check frequency threshold
  if (level.frequency != null) {
    const freqPerWindow = frequencyPerWindow(eventCount, timeRangeMinutes, windowMin);
    if (freqPerWindow >= level.frequency) {
      alerts.push({
        id: `${behaviorKey}-freq-${severity}-${Date.now()}`,
        type: behaviorType,
        severity,
        title: formatAlertTitle(behaviorKey, severity, 'frequency'),
        message: formatAlertMessage(behaviorKey, severity, 'frequency', eventCount, level.frequency, timeRangeMinutes, level.frequencyWindow),
        detectedAt: new Date(),
        threshold: level.frequency,
        actualValue: freqPerWindow,
        metricType: 'frequency',
        behaviorKey,
        contactNotified: false,
        acknowledged: false,
        frequencyWindow: level.frequencyWindow,
      });
    }
  }

  // Check duration threshold
  if (level.duration != null && maxDurationMin >= level.duration) {
    alerts.push({
      id: `${behaviorKey}-dur-${severity}-${Date.now()}`,
      type: behaviorType,
      severity,
      title: formatAlertTitle(behaviorKey, severity, 'duration'),
      message: formatAlertMessage(behaviorKey, severity, 'duration', maxDurationMin, level.duration, timeRangeMinutes),
      detectedAt: new Date(),
      threshold: level.duration,
      actualValue: maxDurationMin,
      metricType: 'duration',
      behaviorKey,
      contactNotified: false,
      acknowledged: false,
    });
  }

  return alerts;
}

// ─── Utility: get current alert status for a behavior ─────────────────────────

export type AlertStatus = 'none' | 'warning' | 'critical';

/**
 * Determine the worst alert status for a given behavior based on current data.
 * Returns 'critical' if any critical threshold is exceeded,
 * 'warning' if any warning threshold is exceeded,
 * 'none' otherwise.
 */
export function getAlertStatus(
  _behaviorKey: AlertBehaviorKey,
  config: BehaviorThreshold,
  metrics: BehaviorMetrics,
  timeRangeMinutes: number,
): AlertStatus {
  if (!config.enabled) return 'none';

  // Check critical first
  if (config.critical) {
    const criticalWindowMin = windowToMinutes(config.critical.frequencyWindow);
    if (config.critical.frequency != null) {
      const freqPerWindow = frequencyPerWindow(metrics.frequency, timeRangeMinutes, criticalWindowMin);
      if (freqPerWindow >= config.critical.frequency) return 'critical';
    }
    if (config.critical.duration != null && metrics.maxDuration >= config.critical.duration) return 'critical';
  }

  // Then check warning
  if (config.warning) {
    const warningWindowMin = windowToMinutes(config.warning.frequencyWindow);
    if (config.warning.frequency != null) {
      const freqPerWindow = frequencyPerWindow(metrics.frequency, timeRangeMinutes, warningWindowMin);
      if (freqPerWindow >= config.warning.frequency) return 'warning';
    }
    if (config.warning.duration != null && metrics.maxDuration >= config.warning.duration) return 'warning';
  }

  return 'none';
}

/**
 * Compute metrics for a single behavior — convenience wrapper.
 */
export function computeBehaviorMetrics(
  bars: GanttBarData[],
  behaviorKey: AlertBehaviorKey,
  timeRangeStartMs: number,
  timeRangeEndMs: number,
): BehaviorMetrics {
  return computeMetrics(bars, behaviorKey, timeRangeStartMs, timeRangeEndMs);
}